import { useEffect, useState } from "react";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import { gridSx, LoadingOverlay, useNewLeadsContext } from "../NewLeads";
import {
  assignLeadToClinic,
  getRegularClinicList,
  getUnassignedLeads,
  moveToArchived,
} from "../../../services/newLeads";

const PAGE_SIZE = 10;

const AssignClinicCell = ({
  leadId,
  domain,
  onAssigned,
}: {
  leadId: string | number;
  domain: string;
  onAssigned?: () => void;
}) => {
  const [clinicOptions, setClinicOptions] = useState<any[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string>("");
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [loadedForDomain, setLoadedForDomain] = useState<string | null>(null);

  const normalizedDomain = String(domain ?? "").trim();

  const loadClinics = async () => {
    if (!normalizedDomain) {
      toast.error("Domain missing for this lead.");
      return;
    }
    if (loadingClinics) return;
    if (loadedForDomain === normalizedDomain) return;

    setLoadingClinics(true);
    try {
      const res: any = await getRegularClinicList(normalizedDomain);
      const clinics = Array.isArray(res)
        ? res
        : res?.clinics || res?.data || [];
      setClinicOptions(clinics);
      setLoadedForDomain(normalizedDomain);
    } catch (e: any) {
      toast.error(e?.msg || "Failed to load clinics.");
    } finally {
      setLoadingClinics(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedClinicId) {
      toast.error("Please select a clinic.");
      return;
    }
    setAssigning(true);
    try {
      await assignLeadToClinic({ clinicId: selectedClinicId, leadId });
      toast.success("Assigned successfully.");
      setSelectedClinicId("");
      onAssigned?.();
    } catch (e: any) {
      toast.error(e?.msg || "Failed to assign lead.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 12, width: "100%" }}
    >
      <select
        value={selectedClinicId}
        onChange={(e) => setSelectedClinicId(e.target.value)}
        onFocus={loadClinics}
        onMouseDown={loadClinics}
        disabled={loadingClinics}
        style={{
          border: "1px solid #d1d5db",
          borderRadius: 6,
          padding: "5px 10px",
          fontSize: 13,
          color: "#374151",
          background: "#fff",
          minWidth: 160,
          width: 180,
        }}
      >
        <option value="">
          {loadingClinics ? "Loading..." : "Select clinic..."}
        </option>
        {clinicOptions.map((c: any) => (
          <option key={c.id ?? c.clinicId} value={c.id ?? c.clinicId}>
            {c.name ?? c.clinicName}
          </option>
        ))}
      </select>
      <button
        onClick={handleAssign}
        disabled={assigning || !selectedClinicId}
        style={{
          border: "none",
          background: "none",
          color: "#194E5B",
          fontWeight: 600,
          cursor: assigning || !selectedClinicId ? "not-allowed" : "pointer",
          fontSize: 13,
          opacity: assigning || !selectedClinicId ? 0.5 : 1,
          whiteSpace: "nowrap",
        }}
      >
        {assigning ? "Assigning..." : "Assign"}
      </button>
    </div>
  );
};

const ArchiveDialog = ({
  lead,
  onClose,
  onArchived,
}: {
  lead: any | null;
  onClose: () => void;
  onArchived: () => void;
}) => {
  const [reason, setReason] = useState("");
  const [archivedBy, setArchivedBy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!lead) {
      setReason("");
      setArchivedBy("");
    }
  }, [lead]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error("Please enter a reason.");
      return;
    }
    if (!archivedBy.trim()) {
      toast.error("Please enter archived by.");
      return;
    }
    setSubmitting(true);
    try {
      await moveToArchived({
        id: lead.id,
        archivedNotes: {
          reason: reason.trim(),
          archivedBy: archivedBy.trim(),
          archivedDate: new Date().toISOString(),
        },
      });
      toast.success("Lead archived successfully.");
      onArchived();
      onClose();
    } catch (e: any) {
      toast.error(e?.msg || "Failed to archive lead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!lead}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { borderRadius: 10 } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#194e5b",
          color: "white",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          fontSize: 15,
          fontWeight: 600,
          py: 1.5,
          px: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Archive Lead
        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
              Reason <span style={{ color: "#e53935" }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Enter reason for archiving..."
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 14,
                color: "#111827",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
              Archived By <span style={{ color: "#e53935" }}>*</span>
            </label>
            <input
              type="text"
              value={archivedBy}
              onChange={(e) => setArchivedBy(e.target.value)}
              placeholder="e.g. concierge"
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 14,
                color: "#111827",
                outline: "none",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                border: "1px solid #d1d5db",
                borderRadius: 8,
                background: "#fff",
                color: "#374151",
                fontSize: 14,
                fontWeight: 600,
                padding: "9px 20px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                border: "none",
                borderRadius: 8,
                background: submitting ? "#9ca3af" : "#194e5b",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                padding: "9px 20px",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? "Archiving..." : "Archive"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Unassigned = () => {
  const { startDate, endDate } = useNewLeadsContext();

  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [reloadTick, setReloadTick] = useState(0);
  const [archiveLead, setArchiveLead] = useState<any | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    getUnassignedLeads({
      page: paginationModel.page + 1,
      record: paginationModel.pageSize,
      search: debouncedSearch || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    })
      .then((res) => {
        const api = res;
        if (api && api.status && !api.status.error) {
          const leads = api.leads || [];
          const mapped = leads.map((l: any) => ({
            ...l,
            patientName: `${l.firstName || ""} ${l.lastName || ""}`.trim(),
          }));
          setRows(mapped);
          setTotalRows(api.total ?? leads.length ?? 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    startDate,
    endDate,
    reloadTick,
  ]);

  const unassignedColumns: GridColDef[] = [
    {
      field: "patient",
      headerName: "Patient",
      minWidth: 250,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.2,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
            {params.row.patientName}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.phone}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.email}
          </div>
        </div>
      ),
    },
    {
      field: "domainTitle",
      headerName: "Category",
      minWidth: 170,
    },
    {
      field: "preferences",
      headerName: "Preferences",
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.2,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
            {params.row.preferredDays} - {params.row.preferredTime}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 10 }}>
            {params.row.notes}
          </div>
        </div>
      ),
    },
    {
      field: "cityPostal",
      headerName: "City / Postal",
      minWidth: 140,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.5,
            height: "100%",
          }}
        >
          <div style={{ fontWeight: 400, fontSize: 14, color: "#111827" }}>
            {params.row.city}
          </div>
          <div style={{ color: "#9ca3af", fontSize: 12 }}>
            {params.row.zipcode}
          </div>
        </div>
      ),
    },
    {
      field: "source",
      headerName: "Source",
      minWidth: 180,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            lineHeight: 1.5,
            height: "100%",
          }}
        >
          <div style={{ fontSize: 14, color: "#374151" }}>
            {params.row.source ? params.row.source : "--"}
          </div>
          {params.row.declinedClinics?.length > 0 &&
            params.row.declinedClinics.map((c: any) => (
              <span
                key={c.id}
                style={{ color: "#e53935", fontSize: 11, marginTop: 2 }}
              >
                <span style={{ fontWeight: 600 }}>Declined By </span>
                {Array.isArray(c?.clinicName)
                  ? c.clinicName.join(", ")
                  : c?.clinicName || "--"}
              </span>
            ))}
        </div>
      ),
    },
    {
      field: "received",
      headerName: "Received",
      minWidth: 180,
      renderCell: (params) => (
        <span style={{ color: "#374151", fontSize: 13 }}>
          {params.row.createdAt
            ? moment(params.row.createdAt).format("MMM D, YYYY • h:mm a")
            : "--"}
        </span>
      ),
    },
    {
      field: "assignTo",
      headerName: "Assign To",
      minWidth: 250,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <AssignClinicCell
          leadId={params.row.id}
          domain={params.row.domain}
          onAssigned={() => setReloadTick((t) => t + 1)}
        />
      ),
    },
    {
      field: "archive",
      headerName: "",
      width: 110,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setArchiveLead(params.row);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            border: "1px solid #fca5a5",
            borderRadius: 7,
            background: "#fff5f5",
            cursor: "pointer",
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 600,
            color: "#dc2626",
            whiteSpace: "nowrap",
            height: 30,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "#fee2e2";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "#fff5f5";
          }}
        >
          <i className="bi bi-archive" style={{ fontSize: 12 }} />
          Archive
        </button>
      ),
    },
  ];

  return (
    <>
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        margin: 20,
        position: "relative",
      }}
    >
      {loading && <LoadingOverlay />}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <i
            className="bi bi-person-lines-fill"
            style={{ color: "#16a09a", fontSize: 20 }}
          />
          <span style={{ fontWeight: 700, fontSize: 17, color: "#111827" }}>
            New Leads — Unassigned
          </span>
          <span
            style={{
              background: "#f3f4f6",
              borderRadius: 12,
              padding: "2px 10px",
              fontSize: 13,
              fontWeight: 600,
              color: "#374151",
            }}
          >
            {totalRows}
          </span>
        </div>

        {/* Search */}
        <div style={{ position: "relative" }}>
          <i
            className="bi bi-search"
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              fontSize: 14,
              pointerEvents: "none",
            }}
          />
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "9px 14px 9px 36px",
              fontSize: 14,
              color: "#374151",
              background: "#fff",
              width: 280,
              outline: "none",
            }}
          />
        </div>
      </div>

      <DataGrid
        rows={rows}
        columns={unassignedColumns}
        getRowId={(row) => row.id ?? row.requestId ?? row.leadId}
        // checkboxSelection
        disableRowSelectionOnClick
        disableColumnMenu
        paginationMode="server"
        rowCount={totalRows}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        density="standard"
        sx={gridSx}
      />
    </div>

    <ArchiveDialog
      lead={archiveLead}
      onClose={() => setArchiveLead(null)}
      onArchived={() => setReloadTick((t) => t + 1)}
    />
    </>
  );
};
